import React, { useEffect, useState } from "react";
import {
  I2cInput,
  I2cMenuItem,
  I2cSelect,
  I2cRadio,
  I2cRadioButton,
  I2cRadioGroup,
  I2cCheckboxGroup,
  I2cListItem,
  I2cCheckbox,
  I2cTextarea,
  I2cTooltip,
  I2cIcon,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { validationSchema } from "./validationSchema";
import NewProgamForm from "./NewProgramForm";
import NewMigrationForm from "./NewMigrationForm";
import DataMigeration from "./DataMigerations";
import MigerationProgramProjection from "./MigerationProgramProjection";
import {
  reloadCardProgram,
  programType,
  credit_programType,
  commercialPrepaid,
  consumerPrepaid,
  setupType,
  currencies,
  dataMigerationType,
} from "./FieldsListingLabels";

const ProgramInfo = ({
  type,
  setFormValidStates,
  setAddFormData,
  addFormData,
  activeInnerStep,
  formDataJson,
  isFetching,
}) => {
  const [selectedDataMigType, setDataMigTypeChange] = useState(null);
  // Define validation schema using Yup
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      pi_program_name: "",
      pi_program_type: "",
      pi_proposition_value: "",
      pi_segment_value: "",
      pi_setup_type: "new-program",
      pi_type_of_migration: "",
      pi_type_of_processor_name: "",
      pi_current_processor_name: "",
      pi_key_component_custodians: [],
      pi_key_component_custodians_other: "",
      pi_data_migration_attributes: [],
      pi_data_migration_attributes_other: "",
      pi_migration_type: "",
      pi_mig_np_ecount_1: "",
      pi_mig_np_ecount_2: "",
      pi_mig_np_ecount_3: "",
      pi_mig_np_ecount_4: "",
      pi_mig_np_evalue_1: "",
      pi_mig_np_evalue_2: "",
      pi_mig_np_evalue_3: "",
      pi_mig_np_evalue_4: "",
      pi_mig_np_evolume_1: "",
      pi_mig_np_evolume_2: "",
      pi_mig_np_evolume_3: "",
      pi_mig_np_evolume_4: "",
      pi_mig_mp_ecount_1: "",
      pi_mig_mp_ecount_2: "",
      pi_mig_mp_ecount_3: "",
      pi_mig_mp_ecount_4: "",
      pi_mig_mp_evalue_1: "",
      pi_mig_mp_evalue_2: "",
      pi_mig_mp_evalue_3: "",
      pi_mig_mp_evalue_4: "",
      pi_mig_mp_evolume_1: "",
      pi_mig_mp_evolume_2: "",
      pi_mig_mp_evolume_3: "",
      pi_mig_mp_evolume_4: "",
    },
  });

  const fields = [
    "pi_program_name",
    "pi_program_type",
    "pi_proposition_value",
    "pi_segment_value",
    "pi_setup_type",
    "pi_type_of_migration",
    "pi_type_of_processor_name",
    "pi_current_processor_name",
    "pi_key_component_custodians",
    "pi_key_component_custodians_other",
    "pi_data_migration_attributes",
    "pi_data_migration_attributes_other",
    "pi_migration_type",
    "pi_mig_np_ecount_1",
    "pi_mig_np_ecount_2",
    "pi_mig_np_ecount_3",
    "pi_mig_np_ecount_4",
    "pi_mig_np_evalue_1",
    "pi_mig_np_evalue_2",
    "pi_mig_np_evalue_3",
    "pi_mig_np_evalue_4",
    "pi_mig_np_evolume_1",
    "pi_mig_np_evolume_2",
    "pi_mig_np_evolume_3",
    "pi_mig_np_evolume_4",
    "pi_mig_mp_ecount_1",
    "pi_mig_mp_ecount_2",
    "pi_mig_mp_ecount_3",
    "pi_mig_mp_ecount_4",
    "pi_mig_mp_evalue_1",
    "pi_mig_mp_evalue_2",
    "pi_mig_mp_evalue_3",
    "pi_mig_mp_evalue_4",
    "pi_mig_mp_evolume_1",
    "pi_mig_mp_evolume_2",
    "pi_mig_mp_evolume_3",
    "pi_mig_mp_evolume_4",
  ];
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[0]["0_0"][field] ?? "");
      });
      methods.trigger();
    }
  }, [isFetching]);

  // Controlls destructuring from the method
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      0: {
        ...addFormData["0"],
        "0_0": { ...values },
      },
    });
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("9_1", true);
  };
  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("0_0", true);
    else setFormValidStates("0_0", false);
  }, [Object.keys(errors).length]);

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div className="single-field-wrapper section-space halfWidth">
          <Controller
            name="pi_program_name"
            control={control}
            render={({ field }) => (
              <I2cInput
                {...field}
                className="single-input-field"
                placeholder="Program Name"
                required
                onI2cInput={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                  handleValueChanges();
                }}
                size="large"
                name={"pi_program_name"}
              >
                <span slot="label" className="organize-tooltip">
                  <span className="custom-label">
                    {" "}
                    Program Name<sup>•</sup>
                  </span>
                  <I2cTooltip
                    content="Specify the name and URL communicated in the program marketing  material"
                    placement="right-start"
                  >
                    <I2cIcon label="placement" name="cs-linear-info"></I2cIcon>
                  </I2cTooltip>
                </span>
              </I2cInput>
            )}
          />
          {/* <ErrorMessage
            show={
              errors.pi_program_name &&
              methods.formState.touchedFields.pi_program_name
                ? true
                : false
            }
            message={errors.pi_program_name?.message ?? "Invalid input"}
          /> */}
        </div>
        <div className="section-space">
          <span slot="label" className="organize-tooltip mb-16">
            <div className="sub-heading">
              Program Type<sup>•</sup>
            </div>
            <I2cTooltip
              content="The category selected here should coincide with the Program Type you have appliedfor (or received an approval for) from the Payment Network If you plan to launch a Creditor a Debit program, please ask your project manager to provide you the relevant CSD."
              placement="right-start"
            >
              <I2cIcon label="placement" name="cs-linear-info"></I2cIcon>
            </I2cTooltip>
          </span>
          <div className="single-field-wrapper">
            <Controller
              name="pi_program_type"
              control={control}
              render={({ field }) => (
                <I2cRadioGroup
                  onI2cChange={(e) => {
                    methods.setValue("pi_program_type", e.target.value);
                    methods.trigger();
                    handleValueChanges();
                  }}
                >
                  <div i2c-grid="grid gap-sm">
                    {credit_programType.map((cp, index) => (
                      <I2cListItem
                        key={index}
                        i2c-grid="4@md"
                        variant="receipt"
                        label={cp.label}
                        sublabel=""
                        value=""
                        checked={methods.watch("pi_program_type") === cp.value}
                        type="checkbox"
                      >
                        <I2cRadio
                          slot="prefix"
                          name="credit_programType"
                          value={cp.value}
                          filled
                          label=""
                          checked={
                            methods.watch("pi_program_type") === cp.value
                          }
                        />
                      </I2cListItem>
                    ))}
                  </div>
                </I2cRadioGroup>
              )}
            />
          </div>
        </div>
        <div className="section-space">
          <span slot="label" className="organize-tooltip mb-16">
            <div className="sub-heading">
              Program Description<sup>•</sup>
            </div>
            <I2cTooltip
              content="Enter a brief description here to help i2c team understand the
                business case."
              placement="right-start"
            >
              <I2cIcon label="placement" name="cs-linear-info"></I2cIcon>
            </I2cTooltip>
          </span>
          <div i2c-grid="grid gap-sm">
            <div i2c-grid=" 6@md">
              <div className="single-field-wrapper">
                <Controller
                  name="pi_proposition_value"
                  control={control}
                  render={({ field }) => (
                    <I2cTextarea
                      label="What is the value proposition, how is the program different
                      form other options in the market?"
                      {...field}
                      name="pi_proposition_value"
                      resize="none"
                      required
                      onI2cInput={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        methods.trigger();
                        handleValueChanges();
                      }}
                      placeholder="Enter Details"
                      rows={2}
                    />
                  )}
                />
                <ErrorMessage
                  show={
                    errors.pi_proposition_value &&
                    methods.formState.touchedFields.pi_proposition_value
                      ? true
                      : false
                  }
                  message={
                    errors.pi_proposition_value?.message ?? "Invalid input"
                  }
                />
              </div>
            </div>
            <div i2c-grid="6@md">
              <div className="single-field-wrapper">
                <Controller
                  name="pi_segment_value"
                  control={control}
                  render={({ field }) => (
                    <I2cTextarea
                      label="Which segment of the market is this program going to be
                      targeted towards?"
                      {...field}
                      name="pi_segment_value"
                      resize="none"
                      required
                      onI2cInput={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        methods.trigger();
                        handleValueChanges();
                      }}
                      placeholder="Enter Details"
                      rows={2}
                    />
                  )}
                />
                <ErrorMessage
                  show={
                    errors.pi_segment_value &&
                    methods.formState.touchedFields.pi_segment_value
                      ? true
                      : false
                  }
                  message={errors.pi_segment_value?.message ?? "Invalid input"}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="section-space">
          <div className="sub-heading">
            Setup Type<sup>•</sup>
          </div>
          <div className="single-field-wrapper mb-16">
            <Controller
              name="pi_setup_type"
              control={control}
              render={({ field }) => (
                <I2cRadioGroup
                  label="Specify the setup type."
                  {...field}
                  name="pi_setup_type"
                  onI2cChange={(e) => {
                    methods.setValue("pi_type_of_processor_name", "");
                    methods.setValue("pi_type_of_migration", "");
                    field.onChange(e);
                    methods.trigger();
                    handleValueChanges();
                  }}
                >
                  {setupType.map((cp, index) => (
                    <I2cRadioButton
                      key={index}
                      name="pi_setup_type"
                      checked={
                        methods.watch("pi_setup_type") ===
                        cp.toLocaleLowerCase().replace(" ", "-")
                      }
                      value={cp.toLocaleLowerCase().replace(" ", "-")}
                    >
                      {cp}
                    </I2cRadioButton>
                  ))}
                </I2cRadioGroup>
              )}
            />
            <ErrorMessage
              show={
                errors.pi_setup_type &&
                methods.formState.touchedFields.pi_setup_type
                  ? true
                  : false
              }
              message={errors.pi_setup_type?.message ?? "Invalid input"}
            />
          </div>
          {methods.getValues("pi_setup_type") === "migration" && (
            <NewMigrationForm
              type={type}
              control={control}
              handleValueChanges={handleValueChanges}
              methods={methods}
              errors={errors}
            />
          )}
        </div>

        <div className="section-space">
          <DataMigeration
            type={type}
            control={control}
            handleValueChanges={handleValueChanges}
            methods={methods}
            errors={errors}
          />
        </div>

        <div className="section-space">
          <div className="sub-heading mb-16">Data Migration</div>
          <I2cRadioGroup
            onI2cChange={(e) => {
              const value = e.target.value;
              [
                "pi_mig_mp_ecount_1",
                "pi_mig_mp_ecount_2",
                "pi_mig_mp_ecount_3",
                "pi_mig_mp_ecount_4",
                "pi_mig_mp_evalue_1",
                "pi_mig_mp_evalue_2",
                "pi_mig_mp_evalue_3",
                "pi_mig_mp_evalue_4",
                "pi_mig_mp_evolume_1",
                "pi_mig_mp_evolume_2",
                "pi_mig_mp_evolume_3",
                "pi_mig_mp_evolume_4",
                "pi_mig_np_ecount_1",
                "pi_mig_np_ecount_2",
                "pi_mig_np_ecount_3",
                "pi_mig_np_ecount_4",
                "pi_mig_np_evalue_1",
                "pi_mig_np_evalue_2",
                "pi_mig_np_evalue_3",
                "pi_mig_np_evalue_4",
                "pi_mig_np_evolume_1",
                "pi_mig_np_evolume_2",
                "pi_mig_np_evolume_3",
                "pi_mig_np_evolume_4",
              ].forEach((field) => methods.setValue(field, ""));
              methods.setValue("pi_migration_type", value);
              methods.trigger();
              handleValueChanges();
            }}
          >
            {dataMigerationType.map((cp, index) => (
              <I2cRadioButton
                key={index}
                name="pi_migration_type"
                value={cp.toLocaleLowerCase().replace(" ", "-")}
                checked={
                  methods.watch("pi_migration_type") ===
                  cp.toLocaleLowerCase().replace(" ", "-")
                }
              >
                {cp}
              </I2cRadioButton>
            ))}
          </I2cRadioGroup>
        </div>
        {methods.watch("pi_migration_type") === "migration-program" && (
          <>
            <MigerationProgramProjection
              type={type}
              control={control}
              handleValueChanges={handleValueChanges}
              methods={methods}
              errors={errors}
            />
          </>
        )}
        {methods.watch("pi_migration_type") === "new-program" && (
          <NewProgamForm
            control={control}
            handleValueChanges={handleValueChanges}
            methods={methods}
            errors={errors}
          />
        )}
        {methods.watch("pi_migration_type") === "both" && (
          <>
            <div className="section-space">
              <NewProgamForm
                control={control}
                handleValueChanges={handleValueChanges}
                methods={methods}
                errors={errors}
              />
            </div>
            <MigerationProgramProjection
              type={type}
              control={control}
              handleValueChanges={handleValueChanges}
              methods={methods}
              errors={errors}
            />
          </>
        )}
      </div>
    </React.Fragment>
  );
};

export default ProgramInfo;
